import type { TransactionRow } from '@/api/hooks/useTransactions';
import type { LinkCandidate } from '@/api/hooks/useLinkCandidates';
import { scopeHref } from '@/app/shell/scope';
import { decimalIsZero, decimalSubtract, decimalSum } from './decimal';
import type { SpendAggregationGroup } from './spendAggregation';

export function matchesCategoryFilter(row: TransactionRow, filter: string): boolean {
  if (filter === 'uncategorized') {
    return row.line_items.length === 0 || row.line_items.some((item) => item.category_slug === null);
  }
  if (filter === 'unallocated') {
    if (row.line_items.length === 0) return false;
    const allocated = decimalSum(row.line_items.map((item) => item.amount));
    return !decimalIsZero(decimalSubtract(row.amount, allocated));
  }
  return row.line_items.some((item) => item.category_slug === filter);
}

export function matchesMerchantFilter(row: TransactionRow, filter: string): boolean {
  if (filter.startsWith('raw:')) return row.merchant_raw === filter.slice(4);
  return row.merchant?.merchant_id === filter;
}

export function matchesCategoryIdFilter(row: TransactionRow, categoryId: string): boolean {
  return row.line_items.some((item) => item.category_id === categoryId);
}

export function matchesMerchantIdFilter(row: TransactionRow, merchantId: string): boolean {
  return row.merchant?.merchant_id === merchantId;
}

/** Consume the server drilldown descriptor; do not invent a second URL grammar. */
export function hrefForSpendDrilldown(
  search: string,
  hash: string,
  descriptor: SpendAggregationGroup['drilldown'],
): string {
  const href = scopeHref(`/transactions${hash}`, search);
  const url = new URL(href, 'http://local.invalid');
  url.searchParams.delete('category');
  url.searchParams.delete('merchant');
  url.searchParams.delete('category_id');
  url.searchParams.delete('merchant_id');
  if (descriptor.category_id) url.searchParams.set('category_id', descriptor.category_id);
  else if (descriptor.merchant_id) url.searchParams.set('merchant_id', descriptor.merchant_id);
  return `${url.pathname}${url.search}${url.hash}`;
}

export interface TransactionScope {
  accountId?: string | null;
  dateFrom?: string;
  dateTo?: string;
}

export function candidateInTransactionScope(candidate: LinkCandidate, scope: TransactionScope): boolean {
  return candidate.legs.some((leg) => {
    if (Number(leg.amount) >= 0) return false;
    if (scope.accountId && leg.account.account_id !== scope.accountId) return false;
    const postedDate = leg.posted_at.slice(0, 10);
    if (scope.dateFrom && postedDate < scope.dateFrom.slice(0, 10)) return false;
    if (scope.dateTo && postedDate > scope.dateTo.slice(0, 10)) return false;
    return true;
  });
}
